import { DetailRow } from "../../DetailRow/DetailRow";
import { StatusBadge } from "../../StatusBadge/StatusBadge";
import { otpWindow } from "../../../utils/otpWindow";
import styles from "./PendingLinkRow.module.scss";

type Props = {
  label: string;
  /** `…OtpValidUntil` of the link; nothing is rendered without one. */
  validUntil?: string | null;
  /** Validity period the auth-server gave the link — see `otpWindow.ts`. */
  ttlMs: number;
  /** Product the link was issued for, if known (e.g. from invitationFrontendUrl). */
  product?: string | null;
  /** Injectable clock for tests. */
  now?: Date;
};

const fmt = (d: Date) => d.toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });

/**
 * One row of the identity card for an open one-time link (invitation or
 * password reset): whether it is still usable, when it expires and — derived
 * from the link's validity period, as the schema stores no creation time —
 * roughly when it was created. The link itself is not shown: the OTP fields are
 * `@private` in the schema and not readable from here.
 */
export function PendingLinkRow({ label, validUntil, ttlMs, product, now }: Props) {
  const value = (
    <PendingLinkValue validUntil={validUntil} ttlMs={ttlMs} product={product} now={now} />
  );
  if (!otpWindow(validUntil, ttlMs, now)) {
    return null;
  }
  return <DetailRow label={label}>{value}</DetailRow>;
}

/** Badge + times of an open one-time link, for use inside any row. */
export function PendingLinkValue({ validUntil, ttlMs, product, now }: Omit<Props, "label">) {
  const w = otpWindow(validUntil, ttlMs, now);
  if (!w) {
    return null;
  }
  return (
    <span className={styles.row}>
      <StatusBadge status={w.expired ? "err" : "warn"} label={w.expired ? "Abgelaufen" : "Offen"} />
      <span className={styles.times}>
        <span>
          {product ? `für ${product} · ` : ""}
          {w.expired ? `abgelaufen am ${fmt(w.validUntil)}` : `gültig bis ${fmt(w.validUntil)}`}
        </span>
        <span className={styles.muted}>erstellt ca. {fmt(w.createdAt)}</span>
      </span>
    </span>
  );
}
